import { useState } from "react";
import { Zap, AlertTriangle, Clock3, Wrench, Truck, Upload, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { EMIRATES, WHATSAPP_URL } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { SubmitBar } from "../components/SubmitBar";
import api from "../lib/api";

export default function UrgentRequest() {
  const { t, lang } = useLang();
  const u = t.urgent;

  const [form, setForm] = useState({
    contact_name: "",
    phone: "",
    email: "",
    business_name: "",
    emirate: "dubai",
    equipment_type: "",
    urgency: "same_day",
    problem: "downtime",
    problem_description: "",
    photo_path: "",
    photo_name: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_path", data.path);
      set("photo_name", data.filename);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const payload = {
    name: form.contact_name,
    phone: form.phone,
    project_type: "restaurant",
    emirate: form.emirate,
    message: `URGENT (${form.urgency}): ${form.problem} — ${form.equipment_type || "unspecified"}`,
    email: form.email,
    company_name: form.business_name,
    preferred_contact: "call",
    boq_file_path: form.photo_path,
    boq_file_name: form.photo_name,
  };
  const extra = {
    urgency: form.urgency,
    problem: form.problem,
    equipment_type: form.equipment_type,
    problem_description: form.problem_description,
  };

  return (
    <div data-testid="urgent-page">
      {/* Alert Hero */}
      <section className="relative bg-[#0f172a] text-white overflow-hidden">
        {/* Diagonal red stripe pattern */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: "repeating-linear-gradient(45deg, #ef4444 0px, #ef4444 20px, transparent 20px, transparent 40px)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid lg:grid-cols-3 gap-10 items-center">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 bg-[#ef4444] text-white px-3 py-1.5 rounded text-[10px] font-bold tracking-[0.3em] uppercase mb-5 animate-pulse">
                <AlertTriangle className="w-3.5 h-3.5" />
                {u.eyebrow}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4">
                {u.title}
              </h1>
              <p className="text-white/80 text-lg leading-relaxed mb-6 max-w-2xl">{u.subtitle}</p>
              <div className="flex flex-wrap gap-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
                  <Button data-testid="urgent-whatsapp" className="bg-green-500 hover:bg-green-600 text-white h-12 px-6 font-semibold">
                    <MessageCircle className="w-4 h-4 me-2" /> {u.whatsappNow}
                  </Button>
                </a>
                <a href="tel:+97142882777">
                  <Button data-testid="urgent-call" variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#0f172a] h-12 px-6 font-semibold">
                    <Zap className="w-4 h-4 me-2" /> {u.callNow}
                  </Button>
                </a>
              </div>
            </div>
            <div className="lg:pl-6 border-s border-white/10">
              <div className="text-[10px] tracking-[0.3em] text-red-300 font-semibold uppercase mb-4">
                {u.slaLabel}
              </div>
              {u.sla.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/10 last:border-b-0">
                  <span className="text-sm text-white/80">{s.label}</span>
                  <span className="font-display text-lg text-red-300">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common problems */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {u.problemsEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] leading-tight">{u.problemsTitle}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {u.problems.map((p, i) => {
              const Icon = [Wrench, Truck, AlertTriangle, Clock3][i] || Wrench;
              return (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid={`urgent-problem-${i}`}>
                  <Icon className="w-6 h-6 text-[#ef4444] mb-3" />
                  <h3 className="font-display text-sm text-[#0891b2] mb-1">{p.title}</h3>
                  <p className="text-slate-600 text-xs leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="request" className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-2xl border-2 border-[#ef4444]/20 shadow-lg overflow-hidden">
            <div className="bg-[#ef4444] text-white p-6">
              <div className="inline-block text-[10px] tracking-[0.3em] text-red-100 font-semibold mb-2 uppercase">
                {u.formEyebrow}
              </div>
              <h2 className="font-display text-2xl">{u.formTitle}</h2>
              <p className="text-white/80 text-sm mt-1">{u.formSubtitle}</p>
            </div>
            <div className="p-6 sm:p-8 space-y-5">
              <Field label={u.urgency} required>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "same_day", color: "bg-[#ef4444] text-white border-[#ef4444]" },
                    { key: "next_48h", color: "bg-orange-500 text-white border-orange-500" },
                    { key: "this_week", color: "bg-amber-500 text-white border-amber-500" },
                  ].map(({ key, color }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => set("urgency", key)}
                      data-testid={`urgent-u-${key}`}
                      className={`py-3 rounded-lg text-xs font-semibold transition-all border ${
                        form.urgency === key ? color + " shadow-md" : "bg-white text-slate-600 border-slate-200 hover:border-[#ef4444]"
                      }`}
                    >
                      {u.urgencies[key]}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label={u.problem} required>
                <Select value={form.problem} onValueChange={(v) => set("problem", v)}>
                  <SelectTrigger data-testid="urgent-p" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["downtime", "delivery", "installation", "replacement", "spare_parts", "other"].map((k) => (
                      <SelectItem key={k} value={k}>{u.problems_options[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label={u.equipmentType}>
                <Input value={form.equipment_type} onChange={(e) => set("equipment_type", e.target.value)} data-testid="urgent-eq" className="h-11" placeholder={u.equipmentTypeHint} />
              </Field>

              <Field label={u.description} required>
                <Textarea required rows={4} value={form.problem_description} onChange={(e) => set("problem_description", e.target.value)} data-testid="urgent-desc" placeholder={u.descriptionHint} />
              </Field>

              <Field label={u.photo}>
                <div>
                  {form.photo_name ? (
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                      <span dir="ltr">📎 {form.photo_name}</span>
                      <button type="button" onClick={() => { set("photo_path", ""); set("photo_name", ""); }} className="text-slate-500 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded p-6 cursor-pointer hover:border-[#ef4444] hover:bg-red-50 transition-colors">
                      <Upload className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-600">{uploading ? "Uploading..." : u.photoHint}</span>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} disabled={uploading} data-testid="urgent-file" className="hidden" />
                    </label>
                  )}
                </div>
              </Field>

              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pb-2 border-b border-slate-100 pt-4">
                {u.contactSection}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={u.contactName} required>
                  <Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="urgent-name" className="h-11" />
                </Field>
                <Field label={u.businessName}>
                  <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="urgent-biz" className="h-11" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label={u.phone} required>
                  <Input required dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="urgent-phone" className="h-11" placeholder="+971 4-288-2777" />
                </Field>
                <Field label={u.email}>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="urgent-email" className="h-11" />
                </Field>
                <Field label={u.emirate} required>
                  <Select value={form.emirate} onValueChange={(v) => set("emirate", v)}>
                    <SelectTrigger data-testid="urgent-em" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EMIRATES.map((e) => <SelectItem key={e} value={e}>{t.emirates[e]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <SubmitBar payload={payload} source="urgent" submitLabel={u.submit} extraData={extra} className="w-full sm:w-auto" />
                <p className="text-xs text-slate-500 mt-3">{u.formFooter}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
